import { getData } from '../utils/ajax-utils';
import cookiesManager from '../utils/cookies-manager';
import { uniqid } from '../utils/utils';

declare const script_var: { ajax_url: string };

/**
 * Définit le cookie `search_nb_per_page` lorsqu'on change la valeur du select
 * permettant de choisir combien de résultats sont affichés sur la page de recherche
 * 
 * @return {void}
 */
export function initSearchNbPerPageSelect() : void {
    document.querySelectorAll<HTMLSelectElement>('.js-search-per-page-select').forEach($select => {
        $select.addEventListener('change', () => {
            cookiesManager.setCookie('search_nb_per_page', $select.value);
            location.reload();
        });
    });
}

/**
 * Initialise l'autocompletion sur les formulaires de recherche
 * 
 * @return {void}
 */
export function initSearchAutocomplete() : void {
    new SearchAutocomplete(document.querySelectorAll<HTMLFormElement>('.js-search-form'));
}

type AutocompleteResultType = { post_title: string, post_url: string };
type SearchFormType = {
    $searchForm: HTMLFormElement,
    $input: HTMLInputElement,
    $autocompleteContainer: HTMLUListElement,
    oldValue: string,
    timeoutCallback: ReturnType<typeof setTimeout> | null,
};

class SearchAutocomplete {
    forms: Record<string, SearchFormType> = {};
    resultsCache: Record<string, AutocompleteResultType[]> = {};
    abortController: AbortController | null = null;

    constructor($searchForms: NodeListOf<HTMLFormElement>) {
        $searchForms.forEach($searchForm => {
            const $input = $searchForm.querySelector<HTMLInputElement>('input');
            const $autocompleteContainer = $searchForm.querySelector<HTMLUListElement>('.js-search-form-autocomplete');
            if (!$input || !$autocompleteContainer) {
                return;
            }
    
            const searchFormUniqId = uniqid();
            this.forms[searchFormUniqId] = {
                $searchForm,
                $input,
                $autocompleteContainer,
                oldValue: $input.value,
                timeoutCallback: null,
            };

            this.initSearchForm(searchFormUniqId);
        });
    }

    /**
     * Initialise le formulaire de recherche
     * 
     * @param {string} searchFormUniqId
     * 
     * @return {void}
     */
    initSearchForm(searchFormUniqId: string) : void {
        const searchForm = this.forms[searchFormUniqId];
        
        // Affiche l'autocomplete lorsque le focus entre dans l'un des éléments du formulaire
        searchForm.$searchForm.addEventListener('focusin', () => {
            if (searchForm.$autocompleteContainer.children.length) {
                searchForm.$autocompleteContainer.classList.remove('hidden');
            }
        });

        // Cache l'autocomplete lorsque le focus sort d'un des éléments du formulaire
        searchForm.$searchForm.addEventListener('focusout', () => {
            setTimeout(() => {
                if (
                    !searchForm.$autocompleteContainer.classList.contains('hidden') &&
                    (!document.activeElement || !searchForm.$searchForm.contains(document.activeElement))
                ) {
                    searchForm.$autocompleteContainer.classList.add('hidden');
                }
            }, 100);
        });

        searchForm.$input.addEventListener('input', () => {
            if (searchForm.$input.value === searchForm.oldValue) {
                return;
            }

            searchForm.$autocompleteContainer.innerHTML = '';

            searchForm.oldValue = searchForm.$input.value;
            searchForm.$autocompleteContainer.classList.add('hidden');

            // Annulation du lancement différé de la recherche précédente
            const timeoutCallback = searchForm.timeoutCallback;
            if (timeoutCallback) {
                clearTimeout(timeoutCallback);
            }

            // Annulation requête précédente si elle n'est pas terminée
            if (this.abortController) {
                this.abortController.abort();
            }
            this.abortController = new AbortController();

            if (searchForm.$input.value.length < 3) {
                // On retire le loading
                searchForm.$searchForm.classList.remove('loading');

                return;
            }

            if (typeof this.resultsCache[searchForm.$input.value] !== 'undefined') {
                // Affiche immédiat des résultats s'ils sont stockés dans le cache
                const results = this.resultsCache[searchForm.$input.value];
                this.displayResults(searchFormUniqId, results);

                // On retire le loading
                searchForm.$searchForm.classList.remove('loading');
            } else {
                // Affichage du loading
                searchForm.$searchForm.classList.add('loading');

                // On attend 5 secondes avant de lancer la recherche
                // (pour laisser l'utilisateur continuer de taper un mot s'il le souhaite)
                searchForm.timeoutCallback = setTimeout(() => {
                    this.getResults(searchForm.$input.value).then(results => {
                        this.displayResults(searchFormUniqId, results);

                        // Sauvegarde des résultats en cache
                        this.resultsCache[searchForm.$input.value] = results;
                    }).catch(() => {}).finally(() => {
                        // On retire le loading et supprime le AbortController
                        searchForm.$searchForm.classList.remove('loading');
                        this.abortController = null;
                    });
                }, 500);
            }
        });
    }

    /**
     * Affiche les résultats
     * 
     * @param {string}                   searchFormUniqId 
     * @param {AutocompleteResultType[]} results 
     * 
     * @return {void}
     */
    displayResults(searchFormUniqId: string, results: AutocompleteResultType[]) : void {
        const $autocompleteContainer = this.forms[searchFormUniqId].$autocompleteContainer;

        // Insertion des résultats dans le DOM
        results.forEach(result => {
            this.insertAutocompleteResultInDOM($autocompleteContainer, result);
        });

        // Affichage de l'autocompletion
        $autocompleteContainer.classList.toggle('hidden', !results.length);
    }

    /**
     * Insert les résultats d'autocompletion dans le DOM
     * 
     * @param {HTMLUListElement}       $autocompleteContainer
     * @param {AutocompleteResultType} result
     * 
     * @return {void}
     */
    insertAutocompleteResultInDOM($autocompleteContainer: HTMLUListElement, result: AutocompleteResultType) : void {
        // Création de l'ancre
        const $anchor = document.createElement('a');
        $anchor.setAttribute('href', result.post_url);
        $anchor.innerText = result.post_title;

        // Création de l'élément de list
        const $li = document.createElement('li');

        // Ajout de l'ancre dans l'élément de liste, puis la liste dans le container de l'autocompletion
        $li.append($anchor);
        $autocompleteContainer.append($li);
    }

    /**
     * Récupère les résultats de recherche via AJAX
     * 
     * @param {string} search
     * 
     * @return {Promise<AutocompleteResultType[]>}
     */
    async getResults(search: string) : Promise<AutocompleteResultType[]> {
        return await getData(script_var.ajax_url, {
            action: 'ajax_get_search_autocompletion',
            search: search,
        }, this.abortController?.signal).then(response => {
            return response.data;
        });
    }
}
